'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { Check, ChevronDown, LayoutGrid, PanelLeftClose, Share2, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from '@/components/i18n-provider';
import {
  UserMenuDropdown,
  type UserMenuSection,
  type UserMenuUser,
} from '@/components/user-menu-dropdown';
import { Avatar } from '@/components/avatar';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { openWorkspacePanel } from '@/components/workspace/panels/panel-events';
import { openWorkspaceSettingsDialog } from '@/components/workspace/workspace-settings-dialog';
import { onWorkspaceListChanged } from '@/lib/workspaces/list-events';
import { useRailV2, useRailAutohide } from '@/lib/workspaces/rail-flag';
import { ShareDialog } from '@/components/sharing/share-dialog';
import { cn } from '@/lib/utils';

type WsCard = { id: string; name: string; updatedAt?: string };

/**
 * Top-left brand card. The Robutler mark itself is the entry point for
 * the user menu — clicking it opens the full account menu (theme,
 * wallet, analytics, settings, sign out).
 *
 * Workspace management (switch / create / rename / delete) lives in the
 * **Workspaces** tab of the left Library panel; the menu's "Workspaces"
 * entry opens the panel there. (This replaces the former in-menu
 * workspace section + the "All workspaces…" manage dialog.)
 *
 * The user menu is portaled into `document.body` so it always floats
 * above the workspace's edge panels.
 */
export function BrandCard({
  className,
  user,
  compact = false,
  avatar = false,
  expanded = false,
}: {
  className?: string;
  user: UserMenuUser;
  /**
   * Rail (V2) variant: render a logo-only square button sized for the
   * 56px rail instead of the wide logo+wordmark+chevron pill. Same menu
   * (workspace switcher + account) underneath.
   */
  compact?: boolean;
  /**
   * Rail account variant: render the user's avatar (used at the bottom of
   * the rail) and open the menu upward. Takes precedence over `compact`.
   */
  avatar?: boolean;
  /**
   * Expanded (labeled) rail: when `avatar` is set, also show the user's
   * name to the right of the avatar.
   */
  expanded?: boolean;
}) {
  const { t } = useTranslation();
  const { openHref } = useFullscreenNav();
  const router = useRouter();
  const pathname = usePathname();
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Rail V2 surfaces an "Appearance" section with the personal auto-hide
  // toggle (a per-device preference, not a workspace setting).
  const railV2 = useRailV2();
  const [autohide, setAutohide] = useRailAutohide();
  // Workspace people-share dialog (Reader/Editor grants). A workspace IS a
  // content folder, so this opens the canonical grant dialog (kind="workspace")
  // which posts to `/api/workspaces/[id]/grants`. Replaces the old link-only
  // `shareLink` action so the rail can grant access to people, not just copy a URL.
  const [shareOpen, setShareOpen] = useState(false);

  // Current workspace id (from the URL) — used to mark the active row.
  const currentId = pathname?.match(/\/workspace\/([0-9a-f-]{36})/i)?.[1] ?? null;

  // Recent workspaces for the user-menu list. Fetched LAZILY — only once the
  // user first opens the menu — so a closed menu costs zero on-load DB work
  // (the workspace page already fires ~15 requests at a max:10 pool on load).
  const [list, setList] = useState<WsCard[]>([]);
  const [menuOpened, setMenuOpened] = useState(false);
  const fetchList = useCallback(() => {
    void fetch('/api/users/me/workspaces')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.workspaces) setList(d.workspaces as WsCard[]);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!menuOpened) return;
    fetchList();
    return onWorkspaceListChanged(fetchList);
  }, [menuOpened, fetchList]);

  const sections = useMemo<UserMenuSection[]>(() => {
    const recent = [...list]
      .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
      .slice(0, 5);
    const items = recent.map((w) => ({
      id: `ws-${w.id}`,
      label: w.name || 'Untitled workspace',
      icon: <LayoutGrid className="h-4 w-4" />,
      onClick: () => router.push(`/workspace/${w.id}`),
      trailing:
        w.id === currentId ? (
          <span className="text-[10px] font-semibold text-primary">● current</span>
        ) : undefined,
    }));
    // "All workspaces…" opens the full Workspaces tab (switch / create / rename
    // / delete all live there now).
    items.push({
      id: 'all-workspaces',
      label: 'All workspaces…',
      icon: <LayoutGrid className="h-4 w-4" />,
      onClick: () => openWorkspacePanel('left', { tab: 'workspaces' }),
      trailing: undefined,
    });
    // Under the rail the account menu does only "you": workspace switching,
    // settings, and share all moved to the Spaces view in the panel. Legacy
    // keeps the "Recent workspaces" + "This workspace" sections here.
    const out: UserMenuSection[] = railV2
      ? []
      : [
      { id: 'recent-workspaces', title: 'Recent workspaces', items },
      {
        id: 'this-workspace',
        title: 'This workspace',
        items: [
          {
            id: 'workspace-settings',
            label: 'Workspace settings',
            icon: <SlidersHorizontal className="h-4 w-4" />,
            onClick: () => openWorkspaceSettingsDialog(),
          },
        ],
      },
    ];
    if (railV2) {
      out.push({
        id: 'appearance',
        title: 'Appearance',
        items: [
          {
            id: 'autohide-rail',
            label: 'Auto-hide sidebar',
            icon: <PanelLeftClose className="h-4 w-4" />,
            onClick: () => setAutohide(!autohide),
            trailing: autohide ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : undefined,
          },
        ],
      });
    }
    return out;
  }, [list, currentId, router, railV2, autohide, setAutohide]);

  // `useMemo` above closes over `setShareOpen` (stable from useState), so it
  // does not need to be in the dep array.

  return (
    // `w-fit` so the wrapper shrink-wraps the trigger pill rather than
    // stretching to the full chrome slot — keeps the onboarding "menu"
    // arrow centred on the visible button, not the wider block div.
    <div
      className={cn(
        'relative pointer-events-auto',
        // Expanded account row must be full-width so the long username
        // truncates instead of overflowing the rail.
        avatar && expanded ? 'w-full' : 'w-fit',
        className,
      )}
    >
      <UserMenuDropdown
        user={user}
        direction={avatar ? 'up' : 'down'}
        width={300}
        // A "Recent workspaces" quick-switch list + an "All workspaces…" link
        // that opens the left Library panel's Workspaces tab — the canonical
        // place to switch, create, rename, and delete workspaces.
        extraSections={sections}
        portal
        openHref={openHref}
        trigger={({ toggle, open }) =>
          avatar ? (
            // Rail account variant — the user's avatar at the bottom of
            // the rail; the menu opens upward (direction="up"). In the
            // expanded rail it also shows the user's name to the right.
            <button
              ref={triggerRef}
              type="button"
              onClick={() => {
                setMenuOpened(true);
                toggle();
              }}
              aria-label={t('nav.account')}
              aria-expanded={open}
              aria-pressed={open}
              className={cn(
                'inline-flex h-10 items-center rounded-xl outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/50',
                expanded ? 'w-full gap-2.5 px-2 text-left' : 'w-10 justify-center',
                open && 'bg-accent/70',
              )}
            >
              <Avatar
                seed={user.username}
                type={user.accountType === 'agent' ? 'agent' : 'human'}
                src={user.avatarUrl}
                className="h-9 w-9 shrink-0"
              />
              {expanded && (
                <span className="min-w-0 flex-1 truncate text-sm text-foreground/80">
                  {user.displayName || user.username}
                </span>
              )}
            </button>
          ) : compact ? (
            // Rail variant — logo-only square target matching the flat
            // `railButtonClass` look (the glass lives on the rail
            // container, not the item).
            <button
              ref={triggerRef}
              type="button"
              onClick={() => {
                setMenuOpened(true);
                toggle();
              }}
              aria-label={t('workspace.canvas.switcherLabel')}
              aria-expanded={open}
              aria-pressed={open}
              data-onboarding-anchor="menu"
              className={cn(
                'relative inline-flex h-9 w-9 items-center justify-center rounded-xl transition-colors select-none outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                open
                  ? 'bg-accent/70 text-foreground'
                  : 'text-foreground/80 hover:text-foreground hover:bg-accent/60',
              )}
            >
              <Image
                src="/icons/Logo_B.svg"
                alt="Robutler"
                width={22}
                height={22}
                className="block dark:hidden"
              />
              <Image
                src="/icons/Logo_W.svg"
                alt="Robutler"
                width={22}
                height={22}
                className="hidden dark:block"
              />
            </button>
          ) : (
          <button
            ref={triggerRef}
            type="button"
            onClick={() => {
              setMenuOpened(true); // lazy-load recent workspaces on first open
              toggle();
            }}
            aria-label={t('workspace.canvas.switcherLabel')}
            aria-expanded={open}
            aria-pressed={open}
            // Onboarding overlay anchor — the sketch `menu` caption
            // pins to the brand pill (logo + workmark + chevron).
            // Stays on the button itself, not the wrapping div, so
            // the rect tracks the actual chrome the user clicks.
            data-onboarding-anchor="menu"
            className={cn(
              'h-10 rounded-2xl px-3 inline-flex items-center gap-2 select-none transition-all',
              // `liquid-glass` ALWAYS applies — even when the menu is
              // open. `.liquid-glass.ws-fab-pressed` (globals.css)
              // overrides the surface tint and inset shadows AND swaps
              // the displacement filter URL to `#liquid-glass-pill-active`
              // so the bezel curve reads as a different refraction
              // pattern in the open state. The glass never disappears.
              'liquid-glass',
              !open && 'hover:shadow-[0_4px_18px_rgba(0,0,0,0.08)]',
              // Open: shared `ws-fab-pressed` so this matches the
              // Create / Activity / Notifications FABs exactly when
              // their respective menus/panels are open — single
              // source of truth for "this is the open one".
              open && 'ws-fab-pressed',
            )}
          >
            <Image
              src="/icons/Logo_B.svg"
              alt="Robutler"
              width={22}
              height={22}
              className="block dark:hidden"
            />
            <Image
              src="/icons/Logo_W.svg"
              alt="Robutler"
              width={22}
              height={22}
              className="hidden dark:block"
            />
            <Image
              src="/icons/Wm_B.svg"
              alt="Robutler"
              width={92}
              height={13}
              className="hidden sm:block dark:hidden"
            />
            <Image
              src="/icons/Wm_W.svg"
              alt="Robutler"
              width={92}
              height={13}
              className="hidden dark:sm:block"
            />
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground transition-transform',
                open && 'rotate-180',
              )}
            />
          </button>
          )
        }
      />
      {shareOpen && currentId && (
        <ShareDialog
          kind="workspace"
          workspaceId={currentId}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}
